import { preloadImage, requestIdleCallback } from '@theme/utilities';

/**
 * Exclusive tab panel switcher.
 *
 * Usage:
 *   <product-tabs>
 *     <button data-tab-prev>Previous</button>
 *     <tab-label-carousel data-tab-track>
 *       <button data-tab-btn aria-controls="panel-id" aria-selected="true">Tab 1</button>
 *     </tab-label-carousel>
 *     <button data-tab-next>Next</button>
 *     <div id="panel-id" data-tab-panel>...</div>
 *   </product-tabs>
 */
class ProductTabsComponent extends HTMLElement {
  /** @type {AbortController} */
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.querySelectorAll('[data-tab-btn]').forEach((button) => {
      button.addEventListener('click', () => this.#selectTab(button), { signal });
    });

    this.querySelectorAll('[data-tab-prev]').forEach((button) => {
      button.addEventListener('click', () => this.#selectRelativeTab(-1), { signal });
    });

    this.querySelectorAll('[data-tab-next]').forEach((button) => {
      button.addEventListener('click', () => this.#selectRelativeTab(1), { signal });
    });

    // Hidden panels use display:none, so lazy images never fetch until selected.
    // Warm the cache in idle time so tab switches stay instant.
    requestIdleCallback(() => this.#preloadPanelImages());
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * @param {Element} activeButton
   */
  #selectTab(activeButton) {
    const targetId = activeButton.getAttribute('aria-controls');
    const buttons = [...this.querySelectorAll('[data-tab-btn]')];
    const index = buttons.indexOf(activeButton);

    this.querySelectorAll('[data-tab-btn]').forEach((button) => {
      button.setAttribute('aria-selected', button === activeButton ? 'true' : 'false');
    });

    this.querySelectorAll('[data-tab-panel]').forEach((panel) => {
      const isActive = /** @type {HTMLElement} */ (panel).id === targetId;
      /** @type {HTMLElement} */ (panel).hidden = !isActive;
    });

    this.dispatchEvent(
      new CustomEvent('product-tabs:change', {
        bubbles: true,
        detail: {
          index,
          button: activeButton,
          panelId: targetId,
        },
      })
    );
  }

  /**
   * @param {number} direction
   */
  #selectRelativeTab(direction) {
    const buttons = [...this.querySelectorAll('[data-tab-btn]')];
    const activeIndex = buttons.findIndex((button) => button.getAttribute('aria-selected') === 'true');
    const nextIndex = (activeIndex + direction + buttons.length) % buttons.length;
    const nextButton = buttons[nextIndex];
    if (!nextButton) return;

    this.#selectTab(nextButton);
    /** @type {HTMLElement} */ (nextButton).focus({ preventScroll: true });
  }

  #preloadPanelImages() {
    /** @type {Set<string>} */
    const urls = new Set();

    this.querySelectorAll('[data-tab-panel] img').forEach((image) => {
      const img = /** @type {HTMLImageElement} */ (image);
      const src = img.getAttribute('src');
      if (src) urls.add(src);

      this.#collectSrcsetUrls(img.getAttribute('srcset'), urls);

      img.closest('picture')?.querySelectorAll('source[srcset]').forEach((source) => {
        this.#collectSrcsetUrls(source.getAttribute('srcset'), urls);
      });
    });

    urls.forEach((url) => preloadImage(url));
  }

  /**
   * @param {string | null} srcset
   * @param {Set<string>} urls
   */
  #collectSrcsetUrls(srcset, urls) {
    if (!srcset) return;

    for (const candidate of srcset.split(',')) {
      const url = candidate.trim().split(/\s+/)[0];
      if (url) urls.add(url);
    }
  }
}

if (!customElements.get('product-tabs')) {
  customElements.define('product-tabs', ProductTabsComponent);
}
